import { API_URL, API_VERSION } from '@constants';
import { useData } from '@hooks/useData';
import { paths } from '../../types/api';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';
import { getSessionId } from '@utils/session';

const ENDPOINT_URL = '/srv-services/to-book/{uuid}';

const ENDPOINT_URL_WITH_VERSION = `${API_VERSION}${ENDPOINT_URL}` as const;
type url = typeof ENDPOINT_URL_WITH_VERSION;

export type GetServicesToBookInput = paths[url]['get']['parameters']['path'];
export type GetServicesToBookSuccessResponse = paths[url]['get']['responses']['200']['content']['application/json'];

/**
 * Requests endpoint to get a list of services that can be pre-booked/booked jointly with a specific service.
 */
export const useGetServicesToBook = () => {
    const { getHeaders, getCommonParameters, handleCommonErrors } = useApiConfig();
    const { setServicesToBook, setIsLoadingServicesToBook } = useData();

    const request = async (props: GetServicesToBookInput) => {
        setIsLoadingServicesToBook(true);

        const sessionId = getSessionId();

        try {
            const commonQueryString = new URLSearchParams(getCommonParameters()).toString();
            const sessionQueryString = `session_id=${encodeURIComponent(sessionId)}`;
            const fullQueryString = `${commonQueryString}&${sessionQueryString}`;

            const response = await fetch(`${API_URL}${ENDPOINT_URL.replace('{uuid}', props.uuid)}?${fullQueryString}`, {
                method: 'GET',
                headers: getHeaders(),
            });

            if (!response.ok) {
                const json = await response.json();
                const error = new Error(json.error || '') as ApiError;
                error.statusCode = response.status;
                throw error;
            }

            const data: GetServicesToBookSuccessResponse = await response.json();

            setServicesToBook(data);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            handleCommonErrors(error);
        } finally {
            setIsLoadingServicesToBook(false);
        }
    };

    return { request };
};
